import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Injector } from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { EnvironmentConfig } from '@core/config-models';
import { TrackToolService } from '@analytics/events';
import { getQueryParameterHistory } from '@core/jss-routing';
import { resetProgressiveForm } from '@innogy/shared/progressive-form';
import {
  GenericProgressiveFormService,
  resetGenericProgressiveFormAction,
  submitGenericProgressiveFormAction,
} from '@innogy/shared/progressive-form/sitecore';
import type { ProgressiveFormFormState } from '@innogy/shared/progressive-form/sitecore/models';
import { genericFormToGenericFormSubmit } from '@innogy/sitecore-forms/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import capitalize from 'lodash/capitalize';
import { type FormGroupControls } from 'ngrx-forms';
import { delay, filter, map, mergeMap, tap, throttle } from 'rxjs/operators';
import type { StoreProductData } from '@legacy/common-ui-models';
import { FehApiActions } from '@feh/shared-api';

import {
  clearStoreFunnelInitializedAction,
  handleStoreFunnelGenericErrorAction,
  onStoreFunnelNavigationAction,
  resetStoreProductSelectionAction,
  selectSelectedStoreProducts,
  selectStoreFunnelSettings,
} from '../store-funnel';
import {
  submitStoreOrderAction,
  trackStoreOrderFormCompleteAction,
} from './store-order-form.actions';
import { parsedStoreOrderFormToolTrackingConfig } from './store-order-form.analytics-config';

@Injectable()
export class StoreOrderFormEffects {
  private readonly actions$ = inject(Actions);
  private readonly store$ = inject(Store);
  private readonly injector = inject(Injector);
  private readonly genericProgressiveFormService = inject(
    GenericProgressiveFormService
  );
  private readonly document: Document = inject(DOCUMENT);
  private readonly config: EnvironmentConfig = inject(ENVIRONMENT_CONFIG);

  funnelSettings$ = this.store$.select(selectStoreFunnelSettings);
  selectedProduct$ = this.store$.select(selectSelectedStoreProducts);
  queryParamHistory$ = this.store$.select(getQueryParameterHistory);

  public readonly onSubmitStoreOrderForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitGenericProgressiveFormAction),
      // throttle until the related API has returned an action, concluding new events can be
      // processed again.
      throttle(() =>
        this.actions$.pipe(
          ofType(
            FehApiActions.postGenericFormSubmit.successAction,
            FehApiActions.postGenericFormSubmit.errorAction
          )
        )
      ),
      concatLatestFrom(() => this.funnelSettings$),
      filter(
        ([action, { orderFormId }]) =>
          orderFormId !== undefined && action.formId === orderFormId
      ),
      concatLatestFrom(([_, { orderFormId }]) => [
        this.selectedProduct$,
        this.selectorForFormId$(orderFormId as string),
      ]),
      mergeMap(
        ([
          [{ toolName }, { apiIntegrationPath, trackingId }],
          selectedProducts,
          formState,
        ]) => {
          const formControls: FormGroupControls<ProgressiveFormFormState> =
            this.genericProgressiveFormService.flattenFormGroupControls(
              formState
            );

          return [
            trackStoreOrderFormCompleteAction({
              formControls,
              toolName,
              stepCount: Object.entries(formState).length,
            }),
            submitStoreOrderAction({
              formControls,
              apiIntegrationPath,
              selectedProducts,
              trackingId: trackingId as string,
            }),
          ];
        }
      )
    )
  );

  public readonly trackStoreOrderFormComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(trackStoreOrderFormCompleteAction),
        tap(({ formControls, stepCount, toolName }) => {
          /**
           * WTA-17: resolve injector context
           */
          const trackToolService = this.injector.get(TrackToolService);
          const config = parsedStoreOrderFormToolTrackingConfig({
            toolName,
            formControls,
            stepCount,
          });
          trackToolService.completeTool(config);
        })
      ),
    { dispatch: false }
  );

  public readonly submitStoreOrderForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(submitStoreOrderAction),
      concatLatestFrom(() => [this.queryParamHistory$]),
      map(([params, queryParamHistory]) =>
        this.mapToGenericFormSubmit(params, queryParamHistory)
      )
    )
  );

  private mapToGenericFormSubmit(
    {
      apiIntegrationPath,
      formControls,
      selectedProducts,
      trackingId,
    }: {
      formControls: FormGroupControls<ProgressiveFormFormState>;
      apiIntegrationPath: string;
      trackingId: string;
      selectedProducts: StoreProductData[];
    },
    queryParamHistory: any
  ) {
    const settings = {
      brand: this.config.brand,
      environment: capitalize(this.config.segment),
      referrer: this.document.referrer,
      formSettingsPath: apiIntegrationPath,
      e2eTrackingId: trackingId,
      gclid: queryParamHistory['gclid'] ?? '',
    };

    const additionalFormValues = {
      product: selectedProducts.map((p) => p.suiteCrmId).join(','),
      campagneid: selectedProducts.map((p) => p.campaignId).join(','),
      gewensttypecontract: 'Koop',
      leadtracking: 'DirectOther',
    };

    const payload = genericFormToGenericFormSubmit(
      settings,
      formControls,
      additionalFormValues
    );
    return FehApiActions.postGenericFormSubmit.requestAction({
      payload: {
        requestBody: {
          formSettingsPath: payload.formSettingsPath,
          referrer: payload.referrer,
          omgeving: payload.omgeving,
          e2eTrackingId: payload.e2eTrackingId,
          gclid: payload.gclid,
          bron: 'Online',
          formValues: payload.formValues,
          brand: payload.brand as any,
        },
      },
    });
  }

  public readonly onSubmitStoreOrderFormSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FehApiActions.postGenericFormSubmit.successAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(([, funnelSettings]) => !!funnelSettings.successPage),
      mergeMap(([, funnelSettings]) => [
        onStoreFunnelNavigationAction({
          page: funnelSettings.successPage,
        }),
      ])
    )
  );

  public readonly resetStoreOrderFormAndProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(routerNavigatedAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(
        ([{ payload }, funnelSettings]) =>
          payload.routerState.url === funnelSettings.successPage?.href
      ),
      // Even though we wait for navigated events, apparently
      // an extra delay is needed in order to prevent firing tool-step events
      // after form rest on the former page.
      // .5s is chosen arbitrarily, yet seems to work even under 3g conditions.
      delay(500),
      mergeMap(([, funnelSettings]) => [
        resetGenericProgressiveFormAction({
          payload: funnelSettings.orderFormId as string,
        }),
        resetProgressiveForm({ formId: funnelSettings.orderFormId as string }),
        resetStoreProductSelectionAction(),
        clearStoreFunnelInitializedAction(),
      ])
    )
  );

  public readonly onSubmitStoreOrderFormError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FehApiActions.postGenericFormSubmit.errorAction),
      tap(({ payload }) => {
        console.error(
          'Error while submitting generic store order',
          payload.error
        );
      }),
      map(handleStoreFunnelGenericErrorAction)
    )
  );

  private readonly selectorForFormId$ = (formId: string) => {
    const selector = this.genericProgressiveFormService.selectorForForm(formId);
    return this.store$.select(selector);
  };
}
